import * as fcl from '@blocto/fcl'
import { OKTA_TOKE_LOCALSTORAGE_KEY } from './wishlist.service.api'
import { BE_ACCESS_TOKEN_LOCALSTORAGE_KEY } from 'hooks/useCurrentUser'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL

// https://flipsidecrypto.xyz/edit/queries/f094d385-64d1-4e8d-8fe5-637f756b1485
// https://flipsidecrypto.xyz/edit/queries/a76b77a5-171d-4fa3-8224-fa61b7c4742f
// https://flipsidecrypto.xyz/edit/queries/ee13f64d-cb49-4021-8ef8-5b6537a81f0c

const getSignature = async (signable: any) => {
  const raw = await fetch(`${BACKEND_URL}/sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ signable })
  })

  const response = await raw.json()
  if (raw.status === 422) {
    throw new Error(response.result)
  }

  return response.signature
}

export const serverAuthorization = async (account: any) => {
  const addr = import.meta.env.VITE_AFL_CONTRACTS
  const keyId = 0

  return {
    ...account,
    tempId: `${addr}-${keyId}`,
    addr: fcl.sansPrefix(addr),
    keyId: Number(keyId),
    signingFunction: async (signable: any) => {
      const signature = await getSignature(signable)
      return {
        addr: fcl.withPrefix(addr),
        keyId: Number(keyId),
        signature
      }
    }
  }
}

export const createTransaction = async (flowUser: any, transactionId: any) => {
  const response = await fetch(`${BACKEND_URL}/api/transaction`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      UserAddress: flowUser,
      TransactionId: transactionId
    })
  })
  if (response.status === 500) {
    throw new Error(await response.json())
  }
}

export const sendTxId = async (txId: string, combinationId: number) => {
  const raw = await fetch(`${BACKEND_URL}/api/tx/update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      combinationId: combinationId,
      txId: txId
    })
  })
  const response = await raw.json()
  if (raw.status === 422) {
    throw new Error(response.result)
  }
  return response
}

export const oktaLogin = async (idToken: string) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/auth/okta/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ idToken })
    })

    if (!response.ok) {
      const result = await response.json()
      if (response.status === 401) {
        console.warn('Unauthorized access. Returning empty object.')
        return {}
      }
      throw new Error(result.msg)
    }

    const result = await response.json()
    return result
  } catch (error) {
    console.error('error', error)
    throw error
  }
}

export const registerOktaId = async (
  oktaId: string,
  acceptTerms?: boolean,
  optIn?: boolean
) => {
  let response
  if (!!acceptTerms || !!optIn) {
    response = await fetch(`${BACKEND_URL}/api/okta`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        oktaId: oktaId,
        acceptTerms: acceptTerms ? '1' : '0',
        optIn: optIn ? '1' : '0'
      })
    })
  } else {
    response = await fetch(`${BACKEND_URL}/api/okta`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        oktaId: oktaId
      })
    })
  }
  if (response.status === 500) {
    const r = await response.json()
    console.error('error', r)
    throw new Error(r)
  }
  const result = await response.json()
  return result
}

export const connectMagicWallet = async (DIDToken: string) => {
  const credentials = JSON.parse(
    localStorage.getItem(BE_ACCESS_TOKEN_LOCALSTORAGE_KEY) || '{}'
  ).accessToken

  if (!credentials) {
    throw new Error('No credentials found')
  }
  const response = await fetch(
    `${BACKEND_URL}/api/user-wallets/magic/connect`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${credentials}`
      },
      body: JSON.stringify({ DIDToken })
    }
  )
  return await response.json()
}

export const registerWallet = async (
  walletAddress: string,
  oktaId: string,
  isMagicWallet?: boolean
) => {
  const credentials = JSON.parse(
    localStorage.getItem(BE_ACCESS_TOKEN_LOCALSTORAGE_KEY) || '{}'
  ).accessToken

  if (!credentials) {
    throw new Error('No credentials found')
  }
  if (!walletAddress || !oktaId) return
  const bodyData = {
    walletAddress: walletAddress,
    oktaId: oktaId,
    isMagic: isMagicWallet
  }

  const response = await fetch(`${BACKEND_URL}/api/wallet`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${credentials}`
    },
    body: JSON.stringify(bodyData)
  })

  if (response.status === 200) {
    return await response.json()
  } else {
    try {
      const data = await response.json()
      if (data === 'Already exists') {
        throw new Error('Record already exists')
      } else {
        console.error('data', data)
        throw new Error('Something went wrong while creating.')
      }
    } catch (error) {
      console.error(error)
      // add papertrail({ error, bodyData, response })
      throw new Error('Something went wrong while creating.')
    }
  }
}

export const getUserWallets = async (oktaId?: string) => {
  const oktaCredentials = JSON.parse(
    localStorage.getItem(OKTA_TOKE_LOCALSTORAGE_KEY) || '{}'
  )
  const credentials = JSON.parse(
    localStorage.getItem(BE_ACCESS_TOKEN_LOCALSTORAGE_KEY) || '{}'
  ).accessToken

  if (!credentials) {
    throw new Error('No credentials found')
  }
  const userId = oktaId || oktaCredentials?.idToken?.claims?.sub || undefined
  const queryString = new URLSearchParams(
    userId ? { oktaId: userId } : {}
  ).toString()

  try {
    const response = await fetch(`${BACKEND_URL}/api/wallet?${queryString}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${credentials}`
      }
    })

    if (response.status === 404) {
      return []
    }

    if (!response.ok) {
      const result = await response.json()
      throw new Error(
        result.msg || `Error fetching user wallets. Status: ${response.status}`
      )
    }

    const result = await response.json()
    return result
  } catch (error) {
    if ((error as Error).message.includes('No data found for the provided'))
      return [] // Return empty array if no data found
    console.error('Error fetching user wallets:', (error as Error).message)
    throw error // Re-throw the error to propagate it to the caller
  }
}

export const getUserEvmWalletAddress = async (): Promise<string | null> => {
  const credentials = JSON.parse(
    localStorage.getItem(BE_ACCESS_TOKEN_LOCALSTORAGE_KEY) || '{}'
  ).accessToken
  if (!credentials) return null
  try {
    const response = await fetch(`${BACKEND_URL}/api/user/wallets`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${credentials}`
      }
    })
    if (!response.ok) return null
    const result = await response.json()
    return (result?.walletAddress as string) ?? null
  } catch {
    return null
  }
}

export const lookupWallets = async (addresses: string[]) => {
  if (!addresses.length || addresses.join(',').length == 0) return []
  try {
    const credentials = JSON.parse(
      localStorage.getItem(BE_ACCESS_TOKEN_LOCALSTORAGE_KEY) || '{}'
    ).accessToken

    if (!credentials) {
      throw new Error('No credentials found')
    }
    const response = await fetch(
      `${BACKEND_URL}/api/wallet/filter-by-addresses?walletAddresses=${addresses.join(
        ','
      )}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${credentials}`
        }
      }
    )

    if (!response.ok) {
      return []
    }

    const result = await response.json()
    return result
  } catch (error) {
    console.error('Error fetching user wallets:', (error as Error).message)
    return []
  }
}

export async function claimCollectible(
  promotionId: string,
  favouriteTeam: string,
  targetWallet: string
) {
  const oktaCredentials = JSON.parse(
    localStorage.getItem(OKTA_TOKE_LOCALSTORAGE_KEY) || '{}'
  )
  const oktaId = oktaCredentials?.idToken?.claims?.sub || undefined

  if (!oktaId)
    throw new Error('User not authenticated. Please log in to continue.')

  const response = await fetch(
    `${BACKEND_URL}/api/claim-collectible/${promotionId}?oktaId=${oktaId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ favouriteTeam, targetWallet })
    }
  )

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }

  return await response.json()
}

// pack purchasing
export const precheck = async (
  userAddress: string,
  price: string,
  templateId: string,
  moonpay?: boolean
) => {
  let url = `${BACKEND_URL}/check?address=${userAddress}&price=${price}&templateId=${templateId}`
  if (moonpay) {
    url += `&moonpay=${moonpay}`
  }

  const oktaCredentials = JSON.parse(
    localStorage.getItem(OKTA_TOKE_LOCALSTORAGE_KEY) || '{}'
  )

  const raw = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${oktaCredentials?.accessToken?.accessToken}`
    }
  })

  const response = await raw.json()
  if (raw.status === 422) {
    throw new Error(response.result)
  }
  return response
}

export const getExchangeRate = async () => {
  const raw = await fetch(`${BACKEND_URL}/exchange-rate`)
  const response = await raw.json()
  return response
}

export const getPackSetting = async (
  packId: string | number
): Promise<{ price_usd: string } | null> => {
  try {
    const raw = await fetch(
      `${BACKEND_URL}/api/pack-setting/${encodeURIComponent(String(packId))}`
    )
    if (!raw.ok) return null
    return await raw.json()
  } catch (e) {
    console.warn('[getPackSetting] error', e)
    return null
  }
}

export const getMoonpaySignature = async (url: string) => {
  const raw = await fetch(`${BACKEND_URL}/api/moonpay/generate_signature`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url })
  })
  const response = await raw.json()
  return response.url
}

export const getAllDuplicateIds = async () => {
  try {
    const raw = await fetch(`${BACKEND_URL}/api/flipside/dupe-ids`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })

    if (!raw.ok) {
      // Log error and throw or return empty array based on desired handling
      console.error(
        `Error fetching duplicate IDs: ${raw.status} - ${await raw.text()}`
      )
      return [] // Return empty array on fetch error
    }

    const response = await raw.json()

    // Check if the expected structure exists
    if (
      response &&
      Array.isArray(response) &&
      response.length > 0 &&
      Object.prototype.hasOwnProperty.call(response[0], 'DUPLICATEIDS')
    ) {
      // Return the array from the correct key
      return response[0].DUPLICATEIDS
    } else {
      console.error(
        'Unexpected response structure from duplicate IDs API:',
        response
      )
      return [] // Return empty array if structure is wrong
    }
  } catch (error) {
    console.error('Error processing duplicate IDs response:', error)
    // add papertrail(error)
    return [] // Return empty array on processing error
  }
}

export const getAllUnexchangedDuplicateIds = async () => {
  try {
    const raw = await fetch(
      // replacing with be group api
      // 'https://api.flipsidecrypto.com/api/v2/queries/fc28c868-b9a1-4aae-af5e-73dc2b5bdbbb/data/latest',
      // 'https://flipsidecrypto.xyz/api/v1/queries/e982c618-c5af-4716-9832-ecdb2a89e079/data/latest',
      'https://flipsidecrypto.xyz/api/v1/queries/f094d385-64d1-4e8d-8fe5-637f756b1485/data/latest',

      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    const response = await raw.json()
    return response[0].UNEXCHANGEDDUPLICATENFTIDS
  } catch (error) {
    console.error(error)
    // add papertrail
  }
}

export const submitUserInfo = async (userInfo: any) => {
  try {
    const raw = await fetch(`${BACKEND_URL}/api/user-info/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userInfo)
    })
    const response = await raw.json()
    return response
  } catch (error) {
    console.error(error)
    // add papertrail
  }
}

export const getSiteSettings = async () => {
  try {
    const raw = await fetch(`${BACKEND_URL}/api/site-settings`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
    const response = await raw.json()
    return response.data
  } catch (error) {
    console.error(error)
    // add papertrail(error)
  }
}

export const getFlags = async () => {
  try {
    const raw = await fetch(`${BACKEND_URL}/api/flags`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
    const response = await raw.json()
    return response.data ?? response
  } catch (error) {
    console.error(error)
    // add papertrail(error)
  }
}

export const checkPromoCode = async (promoCode: string) => {
  try {
    const credentials = JSON.parse(
      localStorage.getItem(BE_ACCESS_TOKEN_LOCALSTORAGE_KEY) || '{}'
    ).accessToken

    if (!credentials) {
      throw new Error('No credentials found')
    }

    const raw = await fetch(
      `${BACKEND_URL}/api/check-promo-code/${promoCode}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${credentials}`
        }
      }
    )
    const response = await raw.json()
    return response
  } catch (error) {
    console.error(error)
    // add papertrail(error)
  }
}

export const claimPromoCode = async (
  promoCode: string,
  walletAddress: string
) => {
  try {
    const credentials = JSON.parse(
      localStorage.getItem(BE_ACCESS_TOKEN_LOCALSTORAGE_KEY) || '{}'
    ).accessToken

    if (!credentials) {
      throw new Error('No credentials found')
    }

    const raw = await fetch(`${BACKEND_URL}/api/claim-pack`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${credentials}`
      },
      body: JSON.stringify({ promoCode, walletAddress })
    })
    const response = await raw.json()
    return response
  } catch (error) {
    console.error(error)
    // add papertrail(error)
  }
}
export const getHashedPackMetadata = async (packHash: string) => {
  const raw = await fetch(`${BACKEND_URL}/api/pack-by-hash/${packHash}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  })
  const response = await raw.json()
  // console.log('get hash pack metadata', { response })
  return response
}

// Pack-template metadata from our BE, used as the primary source by
// useUsersNFTs / usePack / usePacksMetadata. Returns the same shape as Flow's
// GET_TEMPLATE_BY_ID Cadence script:
//   { templateId, maxSupply, issuedSupply, immutableData: { name, image, ... } }
// so callers can use the result directly without a transform step.
//
// Returns null on 404 or any network/parse error so the caller can fall back
// to fcl.query(GET_TEMPLATE_BY_ID) for legacy Flow templates.
export const getPackTemplateFromBackend = async (
  templateId: string | number
): Promise<{
  templateId: string
  maxSupply?: number
  issuedSupply?: string
  immutableData?: Record<string, any>
} | null> => {
  try {
    const raw = await fetch(
      `${BACKEND_URL}/templates/pack/${encodeURIComponent(String(templateId))}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    if (!raw.ok) return null
    return await raw.json()
  } catch (e) {
    // Network error → let caller fall back to Flow
    console.warn(
      '[getPackTemplateFromBackend] error, will fall back to Flow',
      e
    )
    return null
  }
}

// Public BE endpoint that joins on-chain template supply (AFLNFT.getTemplate)
// with the MongoDB metadata catalog. Used by the open-pack reveal flow to
// resolve moment template IDs (e.g. 90075/90076/90077) that don't exist on
// Flow. Returns null on any failure so the caller can fall back to Flow's
// GET_TEMPLATE_BY_ID Cadence script for legacy templates.
//
// The BE returns templateId as a number; normalise to string on the way out
// so callers that string-compare templateIds against Flow's UInt64 strings
// keep working.
export const getMomentTemplateFromBackend = async (
  templateId: string | number
): Promise<{
  templateId: string
  maxSupply?: number
  issuedSupply?: number
  immutableData?: Record<string, any>
} | null> => {
  try {
    const raw = await fetch(
      `${BACKEND_URL}/api/crossmint/moments/template/${encodeURIComponent(
        String(templateId)
      )}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    if (!raw.ok) return null
    const body = await raw.json()
    return { ...body, templateId: String(body.templateId) }
  } catch (e) {
    console.warn(
      '[getMomentTemplateFromBackend] error, will fall back to Flow',
      e
    )
    return null
  }
}

// Single-moment lookup by on-chain tokenId for Crossmint / EVM users. The
// legacy useMoment hook calls Flow Cadence (`fcl.query(GET_SINGLE_NFT)`)
// which throws for EVM tokenIds (no AFLNFT contract on Flow), leaving the
// /moment/:id detail page blank — that's why token 42924 (revealed Genesis
// Ball) rendered as a black page. Hitting this BE endpoint instead returns
// the full enriched moment (templateId + Mongo immutableData + ball-
// specific fields when it's a GB).
//
// Returns the NFT<Moment>-shaped object the FE expects, or null on
// 404/network error so the caller can fall back to Flow for legacy users.
export const getCrossmintMoment = async (
  tokenId: string | number
): Promise<Record<string, any> | null> => {
  try {
    const raw = await fetch(
      `${BACKEND_URL}/api/crossmint/moment/${encodeURIComponent(
        String(tokenId)
      )}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    if (!raw.ok) return null
    return await raw.json()
  } catch (e) {
    console.warn('[getCrossmintMoment] error, will fall back to Flow', e)
    return null
  }
}
